import { NotifyErrorWrapper } from '@/helpers/core/notifications'
import { getAllPartStatuses, getMinerDetails, getMinerStatuses, updateMinerParts } from '@/services/rc.service'
import type { IModel } from '@/types/rc/miners'
import { IndividualPart } from '@/types/rc/miners/PartType'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed, type Ref } from 'vue'

// Miner details are per minerId
export const useMinerDetails = (minerId: Ref<number>) => {
  /* Queries */

  // Miner details query (repairs sidebar data backbone)
  const minerDetailsQuery = useQuery({
    queryKey: ['miner-details', minerId],
    queryFn: () => getMinerDetails(minerId.value).catch(NotifyErrorWrapper('Failed to fetch miner details')),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  /* "Static" data */

  // Miner statuses
  const { data: minerStatuses } = useQuery({
    queryKey: ['miner-statuses'],
    queryFn: () => getMinerStatuses().catch(NotifyErrorWrapper('Failed to fetch possible miner statuses')),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  // Miner part statuses
  const { data: partStatuses } = useQuery({
    queryKey: ['part-statuses'],
    queryFn: () => getAllPartStatuses().catch(NotifyErrorWrapper('Failed to fetch possible part statuses')),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  /* Mutations */

  // Mutate single miner detail field
  const minerDetailsFieldMutation = useMutation({
    mutationKey: ['update-miner', minerId],
    mutationFn: async ({
      partType,
      field,
      value,
    }: {
      partType: IndividualPart
      field: string
      value: string | IModel | null
    }) => {
      let updateData
      switch (partType) {
        case IndividualPart.Miner:
          updateData = { [field]: value }
          break
        case IndividualPart.PSU:
        case IndividualPart.CB:
          updateData = {
            [partType.toLowerCase()]: {
              [field]: value,
            },
          }
          break
        case IndividualPart.FAN1:
        case IndividualPart.FAN2:
        case IndividualPart.FAN3:
        case IndividualPart.FAN4:
          updateData = { fans: [{ number: +partType.split(' ')[1], [field]: value }] }
          break
        case IndividualPart.HB0:
        case IndividualPart.HB1:
        case IndividualPart.HB2:
        case IndividualPart.HB3:
          updateData = { hashboards: [{ number: +partType.split(' ')[1], [field]: value }] }
          break
        default:
          break
      }
      return await updateMinerParts({ id: minerId.value.toString(), ...updateData })
    },
  })

  const minerDetails = computed(() => minerDetailsQuery.data.value)

  return {
    minerDetails,
    minerDetailsQuery,
    minerStatuses,
    partStatuses,
    minerDetailsFieldMutation,
  }
}
